// Expert tracks (professional domains). A user can hold levels in many
// tracks simultaneously — Level 8 Security AND Level 5 Cloud is normal.
//
// Tracks are admin-curated. Adding a new track is a Phase 2 admin
// action, not a one-off code change.
//
// The legacy `EXPERT_REVIEW_TRACK = 'domain_expert'` constant in the
// old guards is replaced by the per-track model below — keeping the
// magic string only as an alias for migration.

export const TRACKS = Object.freeze({
  // Cross-cutting engineering tracks
  SECURITY:       'security',
  CLOUD:          'cloud',
  BACKEND:        'backend',
  FRONTEND:       'frontend',
  DATA_AI:        'data_ai',
  ACCESSIBILITY:  'accessibility',
  UX_RESEARCH:    'ux_research',
  PRODUCT:        'product',

  // Industry / vertical tracks
  HEALTHCARE:     'healthcare',
  FINANCE:        'finance',
  PUBLIC_SECTOR:  'public_sector',
  EDUCATION:      'education',
  RETAIL:         'retail',
  MEDIA:          'media',

  // Catch-all for the legacy migration. New work should pick a real track.
  DOMAIN_EXPERT:  'domain_expert'
});

export const TRACK_LIST = Object.freeze(Object.values(TRACKS));

// Pretty names for UI.
export const TRACK_LABELS = Object.freeze({
  [TRACKS.SECURITY]:      'Security & compliance',
  [TRACKS.CLOUD]:         'Cloud architecture & infrastructure',
  [TRACKS.BACKEND]:       'Backend engineering',
  [TRACKS.FRONTEND]:      'Frontend engineering',
  [TRACKS.DATA_AI]:       'Data & AI',
  [TRACKS.ACCESSIBILITY]: 'Accessibility',
  [TRACKS.UX_RESEARCH]:   'UX research & design',
  [TRACKS.PRODUCT]:       'Product strategy',
  [TRACKS.HEALTHCARE]:    'Healthcare',
  [TRACKS.FINANCE]:       'Finance',
  [TRACKS.PUBLIC_SECTOR]: 'Public sector',
  [TRACKS.EDUCATION]:     'Education',
  [TRACKS.RETAIL]:        'Retail',
  [TRACKS.MEDIA]:         'Media',
  [TRACKS.DOMAIN_EXPERT]: 'Domain expert (legacy)'
});

// Tracks that are valid for the marketplace (excludes the legacy alias).
export const MARKETPLACE_TRACKS = Object.freeze(
  TRACK_LIST.filter((t) => t !== TRACKS.DOMAIN_EXPERT)
);

// Read a user's level in a track from the per-track object. Tolerates
// the legacy flat-number shape so guards continue to work during the
// schema migration (M-002).
//
//   user.levels = { security: 8, cloud: 5 }   ← preferred (post-M-002)
//   user.level  = 8                            ← legacy fallback
export const levelInTrack = (user, track) => {
  if (!user) return 0;
  if (user.levels && typeof user.levels === 'object') {
    const n = Number(user.levels[track]);
    if (Number.isFinite(n)) return n;
    // Legacy alias: anyone with the old 'domain_expert' track gets their
    // flat level applied to it.
    if (track === TRACKS.DOMAIN_EXPERT && Number.isFinite(Number(user.level))) {
      return Number(user.level);
    }
    return 0;
  }
  // Pre-migration: the user has a single flat `level` field that we
  // attribute to the legacy DOMAIN_EXPERT track.
  if (track === TRACKS.DOMAIN_EXPERT) return Number(user.level) || 0;
  return 0;
};

// A user's highest level across any track. Useful for cross-track gates
// (e.g. is this user at Guru somewhere?).
export const maxLevel = (user) => {
  if (!user) return 0;
  if (user.levels && typeof user.levels === 'object') {
    const values = Object.values(user.levels).map(Number).filter(Number.isFinite);
    return values.length ? Math.max(...values) : (Number(user.level) || 0);
  }
  return Number(user.level) || 0;
};

// Best level across a list of tracks (used for "any of these tracks at
// L7+" checks).
export const bestLevelAcross = (user, tracks) => {
  if (!Array.isArray(tracks) || tracks.length === 0) return 0;
  return Math.max(0, ...tracks.map((t) => levelInTrack(user, t)));
};

// ── Which track a review is ROUTED to ──────────────────────────────────
//
// A review request carries a `track`, and the reviewer pool is filtered by
// the tracks a reviewer holds. Nothing chose that track from the project:
// the dashboard's request modal offers no picker and sent 'security' for
// every tier, and the service's own fallback was 'security' too. Seen live
// (2026-09-04): an education-learning project raised job rj-836b468c on the
// security track, and the reviewer granted the education track that same
// evening saw "0 in the pool". A category could never reach the reviewer
// accredited for it.
//
// Derived from the project's document category instead, HERE, so the
// service and the dashboard cannot disagree about it. Categories without a
// vertical track of their own go to PRODUCT — a generalist track — and
// never to security, which is a specialism a customer asks for, not a
// default anyone should fall into.
//
// Keys are `RequirementProject.categoryCode` values (document category
// template codes, admin-curated). An unknown code is not an error: it is a
// generalist review.
export const GENERALIST_TRACK = TRACKS.PRODUCT;

export const CATEGORY_TRACKS = Object.freeze({
  'education-learning':        TRACKS.EDUCATION,
  'healthcare-life-sciences':  TRACKS.HEALTHCARE,
  'financial-services':        TRACKS.FINANCE,
  'e-commerce-retail':         TRACKS.RETAIL,
  'marketplace-booking':       TRACKS.RETAIL,
  'media-content':             TRACKS.MEDIA,
  'public-sector-government':  TRACKS.PUBLIC_SECTOR,
  // Live job history already had operators hand-filing these on data_ai.
  'data-analytics':            TRACKS.DATA_AI,
  'ai-machine-learning':       TRACKS.DATA_AI
});

// The track a review of a project in `categoryCode` is routed to when the
// customer did not pick one. Always a marketplace track; never SECURITY by
// default.
export const trackForCategory = (categoryCode) => {
  const code = String(categoryCode || '').trim().toLowerCase();
  return CATEGORY_TRACKS[code] || GENERALIST_TRACK;
};

// Pretty name for a track, tolerant of unknown values so a UI line such as
// "Routed to Education reviewers" never renders a raw enum.
export const trackLabel = (track) => TRACK_LABELS[track] || String(track || '');

// What a request screen needs to say where a review will go, in one object
// both engines' readAnswers payloads carry as `reviewTrack`. `chosen` is
// false because this is the routed answer; a picker, when one exists, can
// override it and say so.
export const reviewRoutingFor = (categoryCode) => {
  const track = trackForCategory(categoryCode);
  return { categoryCode: categoryCode || null, track, label: trackLabel(track), chosen: false };
};

// The categories whose reviews route to `track` — the inverse of
// CATEGORY_TRACKS. The generalist track has no fixed list: every category
// not named above routes to it. Used at approval time to show whether the
// track being granted matches ANY category the applicant is accredited for:
// the two ladders (track grant here, category accreditation in users-service)
// were never compared, so an admin could grant a track that no accredited
// category ever routes to, and the reviewer's queue stayed honestly empty.
export const categoriesForTrack = (track) =>
  Object.entries(CATEGORY_TRACKS).filter(([, t]) => t === track).map(([c]) => c);

// Does a reviewer accredited in `categoryCodes` ever receive work on `track`?
// The generalist track matches any category outside the vertical map.
export const trackReachableFrom = (track, categoryCodes = []) =>
  (categoryCodes || []).some((c) => trackForCategory(c) === track);
